import React, { useState, useEffect, useRef } from 'react';
import { Search, Send, Paperclip, ArrowLeft, MessageCircle, User, X, Check, CheckCheck, Image, File } from 'lucide-react';
import { format, isToday, isYesterday } from 'date-fns';
import { it } from 'date-fns/locale';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function Chat() {
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [showNewChat, setShowNewChat] = useState(false);
  const [users, setUsers] = useState([]);
  const [searchUsers, setSearchUsers] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const pollIntervalRef = useRef(null);

  useEffect(() => {
    fetchConversations();
    
    // Poll for new messages every 3 seconds
    pollIntervalRef.current = setInterval(() => {
      fetchConversations();
      if (selectedConversation) {
        fetchMessages(selectedConversation._id, true);
      }
    }, 3000);
    
    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    };
  }, []);

  // Fetch users when modal opens
  useEffect(() => {
    if (showNewChat) {
      setSearchUsers('');
      fetchUsers('');
    }
  }, [showNewChat]);

  useEffect(() => {
    if (selectedConversation) {
      fetchMessages(selectedConversation._id);
    }
  }, [selectedConversation]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const fetchConversations = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/chat/conversations`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setConversations(data);
      }
      setLoading(false);
    } catch (err) {
      console.error('Error fetching conversations:', err);
      setLoading(false);
    }
  };

  const fetchMessages = async (conversationId, silent = false) => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/chat/conversations/${conversationId}/messages`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        if (!silent || JSON.stringify(data) !== JSON.stringify(messages)) {
          setMessages(data);
        }
      }
    } catch (err) {
      console.error('Error fetching messages:', err);
    }
  };

  const fetchUsers = async (search = '') => {
    try {
      setLoadingUsers(true);
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/chat/users?search=${encodeURIComponent(search)}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setUsers(data);
      }
    } catch (err) {
      console.error('Error fetching users:', err);
    } finally {
      setLoadingUsers(false);
    }
  };

  const handleStartChat = async (userId) => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/chat/conversations?other_user_id=${userId}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const conv = await res.json();
        setShowNewChat(false);
        await fetchConversations();
        
        // Find the conversation with enriched data
        const enrichedConv = conversations.find(c => c._id === conv._id) || {
          _id: conv._id,
          other_user: users.find(u => u.id === userId)
        };
        setSelectedConversation(enrichedConv);
      }
    } catch (err) {
      console.error('Error creating conversation:', err);
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if ((!newMessage.trim() && !selectedFile) || !selectedConversation) return;

    try {
      setSending(true);
      const token = localStorage.getItem('token');
      const formData = new FormData();
      
      if (newMessage.trim()) {
        formData.append('contenuto', newMessage.trim());
      }
      if (selectedFile) {
        formData.append('file', selectedFile);
      }

      const res = await fetch(`${API_URL}/api/chat/conversations/${selectedConversation._id}/messages`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });

      if (res.ok) {
        setNewMessage('');
        setSelectedFile(null);
        await fetchMessages(selectedConversation._id);
        await fetchConversations();
      }
    } catch (err) {
      console.error('Error sending message:', err);
    } finally {
      setSending(false);
    }
  };

  const formatMessageTime = (dateStr) => {
    const date = new Date(dateStr);
    if (isToday(date)) {
      return format(date, 'HH:mm');
    } else if (isYesterday(date)) {
      return 'Ieri ' + format(date, 'HH:mm');
    }
    return format(date, 'dd/MM HH:mm');
  };

  const formatConversationTime = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    if (isToday(date)) {
      return format(date, 'HH:mm');
    } else if (isYesterday(date)) {
      return 'Ieri';
    }
    return format(date, 'dd/MM/yy');
  };

  const currentUserId = JSON.parse(atob(localStorage.getItem('token')?.split('.')[1] || '{}')).user_id;

  // Mobile: show conversation list or chat based on selection
  const showConversationList = !selectedConversation;

  return (
    <div className="h-[calc(100vh-120px)] flex" data-testid="chat-page">
      {/* Conversations List - Hidden on mobile when conversation is selected */}
      <div className={`${showConversationList ? 'flex' : 'hidden'} md:flex flex-col w-full md:w-80 lg:w-96 border-r border-border bg-white`}>
        {/* Header */}
        <div className="p-4 border-b border-border">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold">Chat</h2>
            <button
              onClick={() => setShowNewChat(true)}
              className="btn btn-primary btn-sm"
              data-testid="new-chat-button"
            >
              <MessageCircle size={18} />
              Nuova
            </button>
          </div>
        </div>

        {/* Conversations */}
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="p-4 text-center text-muted-foreground">Caricamento...</div>
          ) : conversations.length === 0 ? (
            <div className="p-8 text-center text-muted-foreground">
              <MessageCircle size={48} className="mx-auto mb-4 opacity-50" />
              <p>Nessuna conversazione</p>
              <p className="text-sm mt-2">Inizia una nuova chat!</p>
            </div>
          ) : (
            conversations.map((conv) => (
              <div
                key={conv._id}
                onClick={() => setSelectedConversation(conv)}
                className={`flex items-center gap-3 p-4 cursor-pointer hover:bg-muted/50 transition-colors border-b border-border ${
                  selectedConversation?._id === conv._id ? 'bg-primary/10' : ''
                }`}
                data-testid="conversation-item"
              >
                {conv.other_user?.foto_url ? (
                  <img
                    src={`${API_URL}${conv.other_user.foto_url}`}
                    alt=""
                    className="w-12 h-12 rounded-full object-cover"
                  />
                ) : (
                  <div className="w-12 h-12 rounded-full bg-primary text-white flex items-center justify-center font-semibold">
                    {conv.other_user?.nome?.charAt(0)}{conv.other_user?.cognome?.charAt(0)}
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between">
                    <h3 className="font-semibold truncate">
                      {conv.other_user?.nome} {conv.other_user?.cognome}
                    </h3>
                    <span className="text-xs text-muted-foreground">
                      {formatConversationTime(conv.last_message_at)}
                    </span>
                  </div>
                  <p className="text-sm text-muted-foreground truncate">
                    {conv.last_message || 'Nessun messaggio'}
                  </p>
                </div>
                {conv.unread_count > 0 && (
                  <div className="w-5 h-5 bg-primary text-white text-xs rounded-full flex items-center justify-center">
                    {conv.unread_count}
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      </div>

      {/* Chat Area */}
      <div className={`${!showConversationList ? 'flex' : 'hidden'} md:flex flex-col flex-1 bg-gray-50`}>
        {selectedConversation ? (
          <>
            {/* Chat Header */}
            <div className="flex items-center gap-3 p-4 bg-white border-b border-border">
              <button
                onClick={() => setSelectedConversation(null)}
                className="md:hidden p-2 hover:bg-muted rounded-lg"
              >
                <ArrowLeft size={20} />
              </button>
              {selectedConversation.other_user?.foto_url ? (
                <img
                  src={`${API_URL}${selectedConversation.other_user.foto_url}`}
                  alt=""
                  className="w-10 h-10 rounded-full object-cover"
                />
              ) : (
                <div className="w-10 h-10 rounded-full bg-primary text-white flex items-center justify-center font-semibold">
                  {selectedConversation.other_user?.nome?.charAt(0)}{selectedConversation.other_user?.cognome?.charAt(0)}
                </div>
              )}
              <div>
                <h3 className="font-semibold">
                  {selectedConversation.other_user?.nome} {selectedConversation.other_user?.cognome}
                </h3>
              </div>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {messages.map((msg) => {
                const isOwn = msg.sender_id === currentUserId;
                return (
                  <div
                    key={msg._id}
                    className={`flex ${isOwn ? 'justify-end' : 'justify-start'}`}
                  >
                    <div className={`max-w-[75%] ${isOwn ? 'order-2' : ''}`}>
                      <div
                        className={`rounded-2xl px-4 py-2 ${
                          isOwn
                            ? 'bg-primary text-white rounded-br-md'
                            : 'bg-white border border-border rounded-bl-md'
                        }`}
                      >
                        {msg.contenuto && <p className="whitespace-pre-wrap">{msg.contenuto}</p>}
                        {msg.file && (
                          <a
                            href={`${API_URL}${msg.file.url}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className={`flex items-center gap-2 mt-2 p-2 rounded-lg ${
                              isOwn ? 'bg-white/20 hover:bg-white/30' : 'bg-muted hover:bg-muted/80'
                            }`}
                          >
                            {msg.file.tipo?.startsWith('image/') ? (
                              <Image size={16} />
                            ) : (
                              <File size={16} />
                            )}
                            <span className="text-sm truncate">{msg.file.nome}</span>
                          </a>
                        )}
                      </div>
                      <div className={`flex items-center gap-1 mt-1 text-xs text-muted-foreground ${isOwn ? 'justify-end' : ''}`}>
                        <span>{formatMessageTime(msg.created_at)}</span>
                        {isOwn && (
                          msg.read ? <CheckCheck size={14} className="text-primary" /> : <Check size={14} />
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
              <div ref={messagesEndRef} />
            </div>

            {/* Selected File Preview */}
            {selectedFile && (
              <div className="px-4 py-2 bg-white border-t border-border">
                <div className="flex items-center gap-2 p-2 bg-muted rounded-lg">
                  <Paperclip size={16} />
                  <span className="text-sm truncate flex-1">{selectedFile.name}</span>
                  <button
                    onClick={() => setSelectedFile(null)}
                    className="p-1 hover:bg-muted-foreground/20 rounded"
                  >
                    <X size={16} />
                  </button>
                </div>
              </div>
            )}

            {/* Message Input */}
            <form onSubmit={handleSendMessage} className="p-4 bg-white border-t border-border">
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="p-2 hover:bg-muted rounded-lg transition-colors"
                  data-testid="attach-file-button"
                >
                  <Paperclip size={20} className="text-muted-foreground" />
                </button>
                <input
                  ref={fileInputRef}
                  type="file"
                  className="hidden"
                  onChange={(e) => setSelectedFile(e.target.files?.[0])}
                  accept="image/*,.pdf,.doc,.docx,.xls,.xlsx"
                />
                <input
                  type="text"
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  placeholder="Scrivi un messaggio..."
                  className="flex-1 px-4 py-2 border border-border rounded-full focus:outline-none focus:ring-2 focus:ring-primary/50"
                  data-testid="message-input"
                />
                <button
                  type="submit"
                  disabled={sending || (!newMessage.trim() && !selectedFile)}
                  className="p-2 bg-primary text-white rounded-full hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  data-testid="send-message-button"
                >
                  <Send size={20} />
                </button>
              </div>
            </form>
          </>
        ) : (
          <div className="hidden md:flex flex-1 items-center justify-center text-muted-foreground">
            <div className="text-center">
              <MessageCircle size={64} className="mx-auto mb-4 opacity-50" />
              <p className="text-lg">Seleziona una conversazione</p>
              <p className="text-sm">o iniziane una nuova</p>
            </div>
          </div>
        )}
      </div>

      {/* New Chat Modal */}
      {showNewChat && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md max-h-[80vh] flex flex-col">
            <div className="flex items-center justify-between p-4 border-b border-border">
              <h3 className="text-lg font-bold">Nuova Chat</h3>
              <button onClick={() => setShowNewChat(false)} className="p-1 hover:bg-muted rounded">
                <X size={20} />
              </button>
            </div>
            
            <div className="p-4 border-b border-border">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={18} />
                <input
                  type="text"
                  placeholder="Cerca colleghi..."
                  value={searchUsers}
                  onChange={(e) => {
                    setSearchUsers(e.target.value);
                    fetchUsers(e.target.value);
                  }}
                  className="w-full pl-10 pr-4 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/50"
                  data-testid="search-users-input"
                />
              </div>
            </div>

            <div className="flex-1 overflow-y-auto">
              {loadingUsers ? (
                <div className="p-8 text-center text-muted-foreground">
                  <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full mx-auto mb-2"></div>
                  <p>Caricamento utenti...</p>
                </div>
              ) : users.length === 0 ? (
                <div className="p-8 text-center text-muted-foreground">
                  <User size={32} className="mx-auto mb-2 opacity-50" />
                  <p>Nessun utente trovato</p>
                </div>
              ) : (
                users.map((user) => (
                  <div
                    key={user.id}
                    onClick={() => handleStartChat(user.id)}
                    className="flex items-center gap-3 p-4 cursor-pointer hover:bg-muted/50 transition-colors border-b border-border"
                    data-testid="user-item"
                  >
                    {user.foto_url ? (
                      <img
                        src={`${API_URL}${user.foto_url}`}
                        alt=""
                        className="w-10 h-10 rounded-full object-cover"
                      />
                    ) : (
                      <div className="w-10 h-10 rounded-full bg-primary text-white flex items-center justify-center font-semibold text-sm">
                        {user.nome?.charAt(0)}{user.cognome?.charAt(0)}
                      </div>
                    )}
                    <div>
                      <h4 className="font-semibold">{user.nome} {user.cognome}</h4>
                      {user.ruolo && (
                        <p className="text-sm text-muted-foreground">{user.ruolo}</p>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Chat;
